import React, {useCallback, useState} from 'react';
import {Box, IconButton, Stack, Tooltip, Typography} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditIcon from "@mui/icons-material/Edit";
import {SubmitHandler, useForm} from "react-hook-form";
import {
  CuentaNotaEstructuradaDetalle,
  OperacionNotaEstructuradaDetalle,
  schemaNotaEstructuradaDetalle
} from "../types/types";
import {zodResolver} from "@hookform/resolvers/zod/dist/zod";
import {
  OperacionNotaEstructuradaCuentaFields,
  OperacionNotaEstructuradaDetalleFields,
  OperacionNotaEstructuradaLimiteFields
} from "../types/fields";
import {HttpCacheOperacion} from "../../../api/httpCacheOperacion";
import {AsyncSelect} from "../../../../../components/forms";
import {numberFormatter} from "../../../../../utils/formatters/numberFormatter";
import {
  OperacionNotaEstructuradaCuentaDetalleUpdate,
  OperacionNotaEstructuradaDetalleUpdate, schemaOperacionNotaEstructuradaCuentaDetalleUpdate,
  schemaOperacionNotaEstructuradaDetalleUpdate
} from "../types/dto";


interface NotaEstructuradaCampoLimiteProps {
  detalle: CuentaNotaEstructuradaDetalle,
  codPais: number,
  permiteEditar: boolean,
  tipoLimite: 'concertacion' | 'DVP' | 'FOP',
  handleConfirm: (data: OperacionNotaEstructuradaCuentaDetalleUpdate, idCuentaDetalle: number) => void
}

type OperacionNotaEstructuradaCamposLimite = {
  [OperacionNotaEstructuradaCuentaFields.ImporteConcertacion]: number;
  [OperacionNotaEstructuradaCuentaFields.ImporteDPV]: number;
  [OperacionNotaEstructuradaCuentaFields.ImporteFOP]: number;
};

const tiposLimite: Record<string, keyof OperacionNotaEstructuradaCamposLimite> = {
  'concertacion': OperacionNotaEstructuradaCuentaFields.ImporteConcertacion,
  'DVP': OperacionNotaEstructuradaCuentaFields.ImporteDPV,
  'FOP': OperacionNotaEstructuradaCuentaFields.ImporteFOP
}

function NotaEstructuradaCampoLimite({
                                       detalle,
                                       codPais,
                                       permiteEditar,
                                       tipoLimite,
                                       handleConfirm
                                     }: NotaEstructuradaCampoLimiteProps) {

  const campoTipoLimite = tiposLimite[tipoLimite]
  const [editable, setEditable] = useState<boolean>(false);
  const [freeSolo, setFreeSolo] = useState<boolean>(false);

  const {control, handleSubmit} = useForm<OperacionNotaEstructuradaCuentaDetalleUpdate>({
    resolver: zodResolver(schemaOperacionNotaEstructuradaCuentaDetalleUpdate),
    defaultValues: {
      ...detalle
    }
  })

  const onUpdateDetalle: SubmitHandler<OperacionNotaEstructuradaCuentaDetalleUpdate> = (data) => {
    handleConfirm(data, detalle.id)
  }

  const getOpciones = useCallback(() => {
      if (tipoLimite === 'concertacion') {
        return HttpCacheOperacion.getLimitesConcertacionNotaEstructurada(codPais)
      } else if (tipoLimite === 'DVP') {
        return HttpCacheOperacion.getLimitesDVPNotaEstructurada(codPais)
      } else {
        return HttpCacheOperacion.getLimitesFOPNotaEstructurada(codPais)
      }
    }
    , [tipoLimite])


  return (
    <Box display='flex' justifyContent='space-between' alignItems='center' flexWrap='nowrap'>
      {
        (permiteEditar && editable) ?
          <Stack direction={'row'}>
            <AsyncSelect
              control={control}
              name={campoTipoLimite}
              label={tipoLimite}
              fullWidth
              optionPrefix={'USD '}
              loadOptions={getOpciones}
              valueField={OperacionNotaEstructuradaLimiteFields.LimiteImporte}
              selectNotNulleable
            />
          </Stack>

          :
          <Typography noWrap variant='body2'>
            {numberFormatter.toStringWithAmount(detalle[campoTipoLimite], 'USD', 0)}
          </Typography>
      }
      {
        permiteEditar &&
        (
          editable ?
            <Box display='flex' justifyContent='center' flexWrap='nowrap'>
              <Tooltip title='Cancelar'>
                <IconButton onClick={() => setEditable(false)}>
                  <CancelIcon color='error'/>
                </IconButton>
              </Tooltip>
              <Tooltip title='Confirmar'>
                <IconButton onClick={handleSubmit(onUpdateDetalle)}>
                  <CheckCircleIcon color='success'/>
                </IconButton>
              </Tooltip>
            </Box>
            :
            <Tooltip title='Editar'>
              <IconButton onClick={() => setEditable(true)}>
                <EditIcon color='info'/>
              </IconButton>
            </Tooltip>
        )
      }
    </Box>
  )
}

export default NotaEstructuradaCampoLimite;